import { toUserMessage } from '../../lib/errors.js';
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonRouter,
} from '@ionic/react';
import { addCircleOutline, calendarOutline, checkmarkCircle, checkmarkOutline, chevronDownOutline, chevronUpOutline, createOutline, downloadOutline, flaskOutline, imageOutline } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CassettoFrequency } from '@trackeat/shared';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useEnqueueEvent, useEnqueueMaster, useEvents, useMasters } from '../../lib/data/queries.js';
import { getSupabase } from '../../lib/supabase.js';
import { palette } from '../../components/design.js';
import { FeatureIntroBlock, isIntroDismissed } from '../../components/FeatureCard.js';
import { CASSETTI, cassettoOf, groupVociByCassetto, isCassettoDone } from '../../lib/haccp/cassetti.js';
import { scheduleCassettoDueReminders, scheduleCleaningReminder } from '../../lib/notifications/cleaningReminder.js';
import {
  advanceMonth,
  advanceWeek,
  cassettoActiveSince,
  monthlyDueState,
  nextDueNotificationAt,
  weeklyDueState,
  type DueState,
} from '../../lib/haccp/cassettoSchedule.js';

const WEEKDAY_FULL = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

function formatDueShort(d: Date): string {
  const wd = d.toLocaleDateString('it-IT', { weekday: 'short' });
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${wd} ${dd}/${mm}`;
}
import { AddVoceModal } from '../../components/cleaning/AddVoceModal.js';
import { CassettoProductModal } from '../../components/cleaning/CassettoProductModal.js';
import { CleaningTaskEditorModal } from '../../components/editors/CleaningTaskEditorModal.js';
import { useOperationalIdentity } from '../../lib/auth/useOperationalIdentity.js';
import { isAssignedToOperationalIdentity } from '../../lib/auth/operationalAssignment.js';
import { useOperationalCheckEvent } from '../../lib/haccp/useOperationalCheckEvent.js';
import { evidenceArchiveHref } from '../../lib/haccp/operationalEvidenceArchive.js';

interface CleaningSchedule {
  id: string;
  title: string;
  frequency: string;
  business_id: string;
  equipment_id: string | null;
  product_id: string | null;
  product_ids?: string[] | null;
  equipment_ids?: string[] | null;
  assigned_user_ids?: string[] | null;
  responsible_role: string | null;
  version?: number;
  assigned_personnel_ids?: string[] | null;
  requires_photo?: boolean;
}

interface CleaningEvent {
  id: string;
  business_id?: string;
  payload: { schedule_id: string; evidence_photo?: { storage_path?: string } };
  client_ts: string;
}

interface NamedRow {
  id: string;
  name: string;
  category?: string | null;
  archived_at?: string | null;
}

interface BusinessRow {
  id: string;
  name?: string | null;
  cleaning_weekly_weekday?: number | null;
  cleaning_monthly_day?: number | null;
  version?: number;
  archived_at?: string | null;
}

const FREQ_HOURS: Record<string, number> = {
  giornaliera: 24,
  settimanale: 168,
  mensile: 720,
};

const DONE_LABEL: Record<CassettoFrequency, string> = {
  giornaliera: 'Fatto oggi',
  settimanale: 'Fatto (settimana)',
  mensile: 'Fatto (mese)',
};

export function CleaningPage(): JSX.Element {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const router = useIonRouter();
  const { data: schedules = [] } = useMasters<CleaningSchedule>('cleaning_schedules');
  const { data: events = [] } = useEvents('cleaning_done', null);
  const { data: foods = [] } = useMasters<NamedRow>('foods');
  const { data: businesses = [] } = useMasters<BusinessRow>('businesses');
  const identity = useOperationalIdentity();
  const operationalEvent = useOperationalCheckEvent();
  const updateMaster = useEnqueueMaster();
  const enqueueEvent = useEnqueueEvent();
  const [undoTarget, setUndoTarget] = useState<{ schedule: CleaningSchedule; frequency: CassettoFrequency } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [addVoceFreq, setAddVoceFreq] = useState<CassettoFrequency | null>(null);
  const [productCassetto, setProductCassetto] = useState<{ frequency: CassettoFrequency; label: string } | null>(null);
  // T-341: override manuale apri/chiudi delle tendine settimanale/mensile
  // (undefined = usa il default calcolato: aperto se scaduto, chiuso altrimenti).
  const [manualOpen, setManualOpen] = useState<Partial<Record<CassettoFrequency, boolean>>>({});
  const [introVersion, setIntroVersion] = useState(0);
  const [editingSchedule, setEditingSchedule] = useState<CleaningSchedule | null>(null);

  const featureKey = 'cleaning_schedules';
  const userId = profile?.id ?? null;
  const businessId = profile?.active_business_id ?? null;
  const showIntro = schedules.length === 0
    && !isIntroDismissed(featureKey, userId, businessId);
  void introVersion;

  const nameById = new Map<string, string>();
  for (const f of foods) nameById.set(f.id, f.name);

  const lastDoneByScheduleMs = new Map<string, number>();
  const lastEventBySchedule = new Map<string, CleaningEvent>();
  for (const e of events as CleaningEvent[]) {
    const ts = Date.parse(e.client_ts);
    const cur = lastDoneByScheduleMs.get(e.payload.schedule_id);
    if (cur === undefined || ts > cur) {
      lastDoneByScheduleMs.set(e.payload.schedule_id, ts);
      lastEventBySchedule.set(e.payload.schedule_id, e);
    }
  }

  const visibleSchedules = useMemo(() => schedules.filter((schedule) => isAssignedToOperationalIdentity(
    schedule.assigned_personnel_ids,
    identity.personnelId,
    identity.canManageOperationalWork,
  )), [identity.canManageOperationalWork, identity.personnelId, schedules]);
  const grouped = groupVociByCassetto(visibleSchedules);
  const nowMs = Date.now();

  // ── T-341: config per-sede dei giorni di scadenza dei cassetti ──────────
  const activeBusiness = businesses.find((b) => b.id === businessId && !b.archived_at) ?? null;
  const weeklyWeekday = activeBusiness?.cleaning_weekly_weekday ?? null;
  const monthlyDay = activeBusiness?.cleaning_monthly_day ?? null;
  const canConfig = profile?.is_platform_owner === true
    || profile?.role === 'consulente'
    || profile?.role === 'ristoratore';
  const nowDate = new Date(nowMs);
  const dueStateByFreq: Partial<Record<CassettoFrequency, DueState>> = {
    settimanale: weeklyDueState(nowDate, weeklyWeekday),
    mensile: monthlyDueState(nowDate, monthlyDay),
  };

  // Cassetto "attivo" = scaduto/da fare ⇒ va mostrato aperto ed evidenziato.
  // Settimanale/mensile: se configurato, attivo finché non tutte le voci sono
  // fatte dal lastDue in poi; se NON configurato → sempre attivo (legacy).
  function cassettoActive(freq: CassettoFrequency): boolean {
    const st = dueStateByFreq[freq];
    if (!st || !st.configured || !st.lastDue) return true; // giornaliera o legacy
    return cassettoActiveSince(grouped[freq], lastDoneByScheduleMs, st.lastDue.getTime());
  }

  // Tendina aperta? override manuale, altrimenti default = attivo.
  function isCassettoOpen(freq: CassettoFrequency): boolean {
    return manualOpen[freq] ?? cassettoActive(freq);
  }

  async function setCassettoDay(kind: 'weekly' | 'monthly', value: number): Promise<void> {
    if (!activeBusiness) { setToast('Sede non selezionata'); return; }
    const col = kind === 'weekly' ? 'cleaning_weekly_weekday' : 'cleaning_monthly_day';
    try {
      await updateMaster.mutateAsync({
        table: 'businesses',
        id: activeBusiness.id,
        op: 'update',
        base_version: activeBusiness.version ?? 0,
        patch: { id: activeBusiness.id, [col]: value },
      });
      setToast('Giorno aggiornato');
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  // T-341: alert cassetti settimanale/mensile nel giorno previsto, su ogni
  // device della sede (notifica locale calcolata dai dati sincronizzati).
  useEffect(() => {
    try {
      const now = new Date();
      const ws = weeklyDueState(now, weeklyWeekday);
      const ms = monthlyDueState(now, monthlyDay);
      const weeklyAt = grouped.settimanale.length > 0 ? nextDueNotificationAt(now, ws, advanceWeek) : null;
      const monthlyAt = grouped.mensile.length > 0 ? nextDueNotificationAt(now, ms, advanceMonth) : null;
      void scheduleCassettoDueReminders({ weeklyAt, monthlyAt });
    } catch {
      // best-effort
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyWeekday, monthlyDay, schedules]);

  // T-231 — promemoria pulizie: ricalcola le voci NON ancora flaggate nella
  // finestra corrente e (ri)schedula la LocalNotification. Best-effort: la
  // notifica non deve mai rompere la pagina. Si ricalcola dagli stessi dati
  // da cui la UI deriva lo stato fatte/da fare (schedules + eventi cleaning_done).
  useEffect(() => {
    try {
      const lastDoneMs = new Map<string, number>();
      for (const e of events as CleaningEvent[]) {
        const ts = Date.parse(e.client_ts);
        const cur = lastDoneMs.get(e.payload.schedule_id);
        if (cur === undefined || ts > cur) lastDoneMs.set(e.payload.schedule_id, ts);
      }
      const now = Date.now();
      let pendingCount = 0;
      for (const s of visibleSchedules) {
        const freq = cassettoOf(s.frequency);
        const last = lastDoneMs.get(s.id);
        const done = last !== undefined && (now - last) / 3_600_000 < (FREQ_HOURS[freq] ?? 24);
        if (!done) pendingCount += 1;
      }
      void scheduleCleaningReminder({ pendingCount });
    } catch {
      // il promemoria è best-effort: mai propagare errori alla pagina
    }
  }, [visibleSchedules, events]);

  function isVoceDone(s: CleaningSchedule, frequency: CassettoFrequency): boolean {
    const last = lastDoneByScheduleMs.get(s.id);
    if (!last) return false;
    return (nowMs - last) / 3_600_000 < (FREQ_HOURS[frequency] ?? 24);
  }

  async function flagCassetto(frequency: CassettoFrequency): Promise<void> {
    if (!user) return;
    const voci = grouped[frequency].filter((s) => !isVoceDone(s, frequency));
    if (voci.length === 0) return;
    try {
      let pendingPhotos = 0;
      for (const s of voci) {
        const result = await operationalEvent.record({
          eventKind: 'cleaning_done',
          evidenceKind: 'cleaning',
          targetId: s.id,
          businessId: s.business_id,
          requiresPhoto: s.requires_photo === true,
          payload: {
            schedule_id: s.id,
            equipment_id: s.equipment_id,
            product_id: (s.product_ids && s.product_ids[0]) ?? s.product_id ?? null,
            performed_by_user_id: user.id,
            operator_name: profile?.full_name?.trim() || user.email || 'Operatore autenticato',
          },
        });
        if (result === 'cancelled') return;
        if (result === 'recorded_pending_photo') pendingPhotos += 1;
      }
      setToast(pendingPhotos > 0
        ? `${voci.length} voci registrate · ${pendingPhotos} foto in attesa di upload`
        : `${voci.length} voci registrate · ${frequency}`);
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  async function undoCleaning(): Promise<void> {
    if (!undoTarget || !identity.canManageOperationalWork) return;
    const { schedule, frequency } = undoTarget;
    const ids = (events as CleaningEvent[]).filter((event) => (
      event.payload.schedule_id === schedule.id
      && event.business_id === schedule.business_id
      && (Date.now() - Date.parse(event.client_ts)) / 3_600_000 < FREQ_HOURS[frequency]
    )).map((event) => event.id);
    if (!ids.length) { setUndoTarget(null); return; }
    try {
      await enqueueEvent.mutateAsync({ kind: 'cleaning_voided', business_id: schedule.business_id,
        payload: { schedule_id: schedule.id, original_client_uuids: ids, reason: 'user_cancel' } });
      setUndoTarget(null);
      setToast(`Spunta rimossa: ${schedule.title}. Annullamento registrato nello storico.`);
    } catch (error) { setToast(toUserMessage(error)); }
  }

  async function flagVoce(
    s: CleaningSchedule,
    frequency: CassettoFrequency,
    repeatMissingPhoto = false,
  ): Promise<void> {
    if (!user) return;
    if (isVoceDone(s, frequency) && !repeatMissingPhoto) return;
    try {
      const result = await operationalEvent.record({
        eventKind: 'cleaning_done',
        evidenceKind: 'cleaning',
        targetId: s.id,
        businessId: s.business_id,
        requiresPhoto: s.requires_photo === true,
        payload: {
          schedule_id: s.id,
          equipment_id: s.equipment_id,
          product_id: (s.product_ids && s.product_ids[0]) ?? s.product_id ?? null,
          performed_by_user_id: user.id,
          operator_name: profile?.full_name?.trim() || user.email || 'Operatore autenticato',
        },
      });
      if (result === 'cancelled') return;
      setToast(result === 'recorded_pending_photo'
        ? `Fatto: ${s.title} · foto in attesa di upload`
        : `Fatto: ${s.title}`);
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  // ── T-123: export mensile A4 landscape stile Cambusa ───────────────────
  const canExportMonth = profile?.is_platform_owner === true
    || profile?.role === 'consulente';
  const now = new Date();
  const [exportOpen, setExportOpen] = useState(false);
  const [exportYear, setExportYear] = useState<number>(now.getFullYear());
  const [exportMonth, setExportMonth] = useState<number>(now.getMonth() + 1);
  const [exporting, setExporting] = useState(false);

  async function handleExportMonth(): Promise<void> {
    if (!profile?.active_tenant_id) {
      setToast('Tenant non selezionato');
      return;
    }
    setExporting(true);
    try {
      const { data, error } = await getSupabase().functions.invoke<{ pdf_url?: string }>(
        'export-cleaning-month',
        { body: { tenant_id: profile.active_tenant_id, year: exportYear, month: exportMonth } },
      );
      if (error) throw error;
      const pdfUrl = data?.pdf_url;
      if (!pdfUrl) throw new Error('Nessun URL restituito');
      window.open(pdfUrl, '_blank', 'noopener,noreferrer');
      setExportOpen(false);
      setToast('Registro mese generato');
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setExporting(false);
    }
  }

  const MONTH_LABELS_IT = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
  ];
  const exportYearOptions = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  const navTiles: { icon: string; label: string; href: string }[] = [
    { icon: '📋', label: 'Da fare', href: '/haccp/cleaning/todo' },
    { icon: '✅', label: 'Fatte', href: '/haccp/cleaning/done' },
    { icon: '🧴', label: 'Prodotti', href: '/haccp/cleaning/products' },
    { icon: '🔧', label: 'Attrezzi', href: '/haccp/cleaning/equipments' },
  ];

  const activeProductCassettoVoci = productCassetto
    ? grouped[productCassetto.frequency].map((s) => ({ id: s.id, version: s.version }))
    : [];
  const activeProductCassettoIds = productCassetto
    ? [...new Set(grouped[productCassetto.frequency].flatMap((s) => s.product_ids ?? []).filter(Boolean))]
    : [];

  return (
    <IonPage>
      <IonAlert isOpen={undoTarget !== null} header="Togliere la spunta?"
        message="La pulizia tornerà da fare. La registrazione originale e il tuo annullamento resteranno nello storico."
        onDidDismiss={() => setUndoTarget(null)}
        buttons={[{ text: 'Indietro', role: 'cancel' }, { text: 'Togli spunta', handler: () => { void undoCleaning(); } }]} />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/today" />
          </IonButtons>
          <IonTitle>{t('haccp.cleaning')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': palette.bg } as React.CSSProperties}>
        <div style={{ padding: '12px 16px 96px' }}>
          {/* Mini-tile di navigazione */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {navTiles.map((nt) => (
              <button
                key={nt.href}
                type="button"
                onClick={() => router.push(nt.href, 'forward')}
                style={{
                  background: palette.card,
                  borderRadius: 12,
                  border: `1px solid ${palette.border}`,
                  padding: '12px 4px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 20 }}>{nt.icon}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: palette.text }}>{nt.label}</span>
              </button>
            ))}
          </div>

          {canExportMonth && (
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                onClick={() => setExportOpen(true)}
                style={{
                  width: '100%',
                  background: palette.card,
                  borderRadius: 12,
                  border: `1px dashed ${palette.borderStrong}`,
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                }}
              >
                <IonIcon icon={downloadOutline} style={{ fontSize: 18, color: palette.text }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: palette.text }}>
                  Esporta registro mese (A4)
                </span>
              </button>
            </div>
          )}

          {showIntro && (
            <FeatureIntroBlock
              featureKey={featureKey}
              icon="🧽"
              title="Pulizie a cassetti"
              description={
                <>
                  Le pulizie sono organizzate in <b>3 cassetti per frequenza</b>:
                  Giornaliera, Settimanale, Mensile — come nel registro HACCP che
                  esporti. Dentro ogni cassetto metti le <b>attrezzature e le aree</b>
                  da pulire; un solo tocco su "Fatto" le registra tutte insieme.
                  <br /><br />
                  Aggiungi le voci dai suggerimenti standard o a testo libero, e
                  associa il <b>detersivo</b> usato per ogni cassetto.
                </>
              }
              whatsappQuestion="Ciao, ho dubbi su come impostare i cassetti di pulizia in TrackEAT, mi puoi aiutare?"
              startLabel="Inizia dal cassetto Giornaliera"
              onStart={() => { setIntroVersion((v) => v + 1); setAddVoceFreq('giornaliera'); }}
              userId={userId}
              businessId={businessId}
              flush
            />
          )}

          {/* ── 3 CASSETTI ── */}
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {CASSETTI.map((cas) => {
              const voci = grouped[cas.frequency];
              const cassettoDone = isCassettoDone(voci, lastDoneByScheduleMs, cas.frequency, nowMs);
              const prodIds = [...new Set(voci.flatMap((v) => v.product_ids ?? []).filter(Boolean))] as string[];
              const prodNames = prodIds.map((id) => nameById.get(id)).filter((n): n is string => !!n);
              // T-341: settimanale/mensile = tendina richiudibile, schedulata.
              const scheduled = cas.frequency === 'settimanale' || cas.frequency === 'mensile';
              const due = dueStateByFreq[cas.frequency];
              const configured = !!due?.configured;
              const active = scheduled ? cassettoActive(cas.frequency) : true;
              const expanded = scheduled ? isCassettoOpen(cas.frequency) : true;
              return (
                <div
                  key={cas.frequency}
                  style={{
                    background: palette.card,
                    borderRadius: 16,
                    borderTop: `4px solid ${cas.color}`,
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 8px rgba(15,23,42,.06)',
                    overflow: 'hidden',
                  }}
                >
                  {/* Header cassetto (settimanale/mensile = toggle tendina) */}
                  <div
                    onClick={scheduled ? () => setManualOpen((m) => ({ ...m, [cas.frequency]: !expanded })) : undefined}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px 6px', cursor: scheduled ? 'pointer' : 'default' }}
                  >
                    <span style={{ fontSize: 18 }}>{cas.emoji}</span>
                    <span style={{ fontSize: 16, fontWeight: 700, color: palette.text, flex: 1 }}>{cas.label}</span>
                    {scheduled && configured && active && !cassettoDone && (
                      <span style={{ fontSize: 12, fontWeight: 700, color: palette.warn }}>Da fare</span>
                    )}
                    {scheduled && configured && !active && !cassettoDone && due?.nextDue && (
                      <span style={{ fontSize: 12, fontWeight: 600, color: palette.textFaint }}>
                        prossima: {formatDueShort(due.nextDue)}
                      </span>
                    )}
                    {cassettoDone && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: palette.success }}>
                        <IonIcon icon={checkmarkCircle} /> Fatto
                      </span>
                    )}
                    {scheduled && (
                      <IonIcon icon={expanded ? chevronUpOutline : chevronDownOutline} style={{ fontSize: 18, color: palette.textFaint }} />
                    )}
                  </div>

                  {/* T-341: riga config "spunta il giorno…" (settimanale/mensile) */}
                  {scheduled && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px 6px' }}>
                      <IonIcon icon={calendarOutline} style={{ fontSize: 15, color: palette.textFaint, flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: palette.textFaint, flexShrink: 0 }}>
                        {cas.frequency === 'settimanale' ? 'Spunta ogni' : 'Spunta il giorno'}
                      </span>
                      {canConfig ? (
                        <IonSelect
                          aria-label={`Giorno cassetto ${cas.label}`}
                          interface="action-sheet"
                          placeholder="— scegli —"
                          value={cas.frequency === 'settimanale' ? (weeklyWeekday ?? undefined) : (monthlyDay ?? undefined)}
                          onClick={(e) => e.stopPropagation()}
                          onIonChange={(e) => {
                            const v = Number(e.detail.value);
                            if (Number.isFinite(v)) void setCassettoDay(cas.frequency === 'settimanale' ? 'weekly' : 'monthly', v);
                          }}
                          style={{ fontSize: 13, minWidth: 130, '--padding-start': '6px' } as React.CSSProperties}
                        >
                          {cas.frequency === 'settimanale'
                            ? WEEKDAY_FULL.map((label, i) => (
                                <IonSelectOption key={i + 1} value={i + 1}>{label}</IonSelectOption>
                              ))
                            : Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                                <IonSelectOption key={d} value={d}>{d}</IonSelectOption>
                              ))}
                        </IonSelect>
                      ) : (
                        <span style={{ fontSize: 13, fontWeight: 600, color: palette.text }}>
                          {cas.frequency === 'settimanale'
                            ? (weeklyWeekday ? WEEKDAY_FULL[weeklyWeekday - 1] : 'non impostato')
                            : (monthlyDay ? `giorno ${monthlyDay}` : 'non impostato')}
                        </span>
                      )}
                    </div>
                  )}

                  {expanded && (<>
                  {/* Voci */}
                  <div style={{ padding: '0 14px' }}>
                    {voci.length === 0 ? (
                      <div style={{ fontSize: 13, color: palette.textFaint, fontStyle: 'italic', padding: '4px 0 10px' }}>
                        Nessuna voce. Aggiungi attrezzature o aree da pulire.
                      </div>
                    ) : (
                      voci.map((s) => {
                        const vdone = isVoceDone(s, cas.frequency);
                        const lastEvent = lastEventBySchedule.get(s.id);
                        const lastEvidence = lastEvent?.payload.evidence_photo?.storage_path ? lastEvent : null;
                        return (
                          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              aria-label={vdone ? 'Annulla registrazione pulizia' : 'Segna voce come fatta'}
                              disabled={vdone && !identity.canManageOperationalWork}
                              onClick={() => vdone ? setUndoTarget({ schedule: s, frequency: cas.frequency }) : void flagVoce(s, cas.frequency)}
                              style={{
                                width: 22, height: 22, borderRadius: 5, padding: 0,
                                border: `2px solid ${vdone ? palette.success : palette.borderStrong}`,
                                background: vdone ? palette.success : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                cursor: vdone && !identity.canManageOperationalWork ? 'default' : 'pointer',
                              }}
                            >
                              {vdone && <IonIcon icon={checkmarkOutline} style={{ color: '#fff', fontSize: 14 }} />}
                            </button>
                            <span style={{ flex: 1, fontSize: 14, color: palette.text, textDecoration: vdone ? 'line-through' : 'none', opacity: vdone ? 0.6 : 1 }}>
                              {s.title}{s.requires_photo ? ' 📸' : ''}
                            </span>
                            {identity.canManageOperationalWork && lastEvidence && (
                              <button
                                type="button"
                                aria-label={`Vedi foto dell'ultimo controllo di ${s.title}`}
                                onClick={() => router.push(evidenceArchiveHref({ kind: 'cleaning', targetId: s.id, clientTs: lastEvidence.client_ts }), 'forward')}
                                style={{ minHeight: 44, background: '#eff6ff', border: `1px solid ${palette.accent}`, borderRadius: 9, cursor: 'pointer', padding: '6px 9px', display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0, color: palette.accent, fontSize: 12, fontWeight: 700 }}
                              >
                                <IonIcon icon={imageOutline} /> Vedi foto
                              </button>
                            )}
                            {vdone && s.requires_photo && !lastEvidence && (
                              <IonButton
                                fill="outline"
                                size="small"
                                color="warning"
                                aria-label={`Ripeti il controllo di ${s.title} con fotografia`}
                                onClick={() => void flagVoce(s, cas.frequency, true)}
                                style={{ minHeight: 44, margin: 0 }}
                              >
                                <IonIcon icon={imageOutline} slot="start" /> Ripeti con foto
                              </IonButton>
                            )}
                            {identity.canManageOperationalWork && (
                              <button
                                type="button"
                                aria-label="Configura voce, assegnazioni e foto"
                                onClick={() => setEditingSchedule(s)}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', flexShrink: 0 }}
                              >
                                <IonIcon icon={createOutline} style={{ fontSize: 18, color: palette.textFaint }} />
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Prodotto del cassetto */}
                  <button
                    type="button"
                    onClick={() => setProductCassetto({ frequency: cas.frequency, label: cas.label })}
                    style={{
                      width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                      borderTop: `1px solid ${palette.border}`, marginTop: 8,
                      padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                    }}
                  >
                    <IonIcon icon={flaskOutline} style={{ fontSize: 16, color: palette.textFaint }} />
                    <span style={{ fontSize: 13, color: prodNames.length ? palette.text : palette.textFaint, flex: 1 }}>
                      {prodNames.length ? `Prodotto: ${prodNames.join(', ')}` : 'Imposta prodotto (detersivo)'}
                    </span>
                    <span style={{ fontSize: 12, color: palette.accent, fontWeight: 600 }}>Cambia</span>
                  </button>

                  {/* Azioni */}
                  <div style={{ display: 'flex', gap: 8, padding: '8px 14px 14px' }}>
                    <button
                      type="button"
                      onClick={() => setAddVoceFreq(cas.frequency)}
                      style={{
                        flex: 1, background: palette.bg, border: `1px solid ${palette.border}`,
                        borderRadius: 10, padding: '11px 8px', fontSize: 13, fontWeight: 600,
                        color: palette.text, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, cursor: 'pointer',
                      }}
                    >
                      <IonIcon icon={addCircleOutline} /> Aggiungi voce
                    </button>
                    <button
                      type="button"
                      disabled={voci.length === 0 || cassettoDone}
                      onClick={() => void flagCassetto(cas.frequency)}
                      style={{
                        flex: 1,
                        background: voci.length === 0 || cassettoDone ? palette.border : cas.color,
                        border: 'none', borderRadius: 10, padding: '11px 8px', fontSize: 13, fontWeight: 700,
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        cursor: voci.length === 0 || cassettoDone ? 'default' : 'pointer',
                        opacity: voci.length === 0 || cassettoDone ? 0.6 : 1,
                      }}
                    >
                      <IonIcon icon={checkmarkOutline} /> {DONE_LABEL[cas.frequency]}
                    </button>
                  </div>
                  </>)}
                </div>
              );
            })}
          </div>
        </div>

        <CleaningTaskEditorModal
          isOpen={editingSchedule !== null}
          initial={editingSchedule ?? undefined}
          onDismiss={() => setEditingSchedule(null)}
          onSaved={() => setToast('Attività aggiornata')}
        />

        <AddVoceModal
          isOpen={addVoceFreq !== null}
          frequency={addVoceFreq ?? 'giornaliera'}
          onDismiss={() => setAddVoceFreq(null)}
          onAdded={() => { /* la lista si aggiorna via query invalidation di useMasters */ }}
        />

        <CassettoProductModal
          isOpen={productCassetto !== null}
          label={productCassetto?.label ?? ''}
          voci={activeProductCassettoVoci}
          currentProductIds={activeProductCassettoIds}
          onDismiss={() => setProductCassetto(null)}
        />

        <IonModal isOpen={exportOpen} onDidDismiss={() => setExportOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setExportOpen(false)} disabled={exporting}>
                  Annulla
                </IonButton>
              </IonButtons>
              <IonTitle>Esporta registro mese</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ fontSize: 13, color: palette.textFaint, lineHeight: 1.4 }}>
                Genera il PDF mensile A4 landscape stile registro Cambusa con
                matrice attrezzature × giorni, controllo temperature, pulizie
                standard, non conformità e firma responsabile.
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: palette.text, display: 'block', marginBottom: 6 }}>
                  Mese
                </label>
                <select
                  value={exportMonth}
                  onChange={(e) => setExportMonth(Number(e.target.value))}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 10,
                    border: `1px solid ${palette.border}`, background: palette.card,
                    color: palette.text, fontSize: 14,
                  }}
                >
                  {MONTH_LABELS_IT.map((label, idx) => (
                    <option key={idx + 1} value={idx + 1}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: palette.text, display: 'block', marginBottom: 6 }}>
                  Anno
                </label>
                <select
                  value={exportYear}
                  onChange={(e) => setExportYear(Number(e.target.value))}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 10,
                    border: `1px solid ${palette.border}`, background: palette.card,
                    color: palette.text, fontSize: 14,
                  }}
                >
                  {exportYearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <IonButton expand="block" onClick={handleExportMonth} disabled={exporting}>
                {exporting ? <IonSpinner name="dots" /> : 'Genera PDF'}
              </IonButton>
            </div>
          </IonContent>
        </IonModal>

        <IonToast
          isOpen={!!toast}
          message={toast ?? ''}
          duration={1500}
          onDidDismiss={() => setToast(null)}
        />
      </IonContent>
    </IonPage>
  );
}
